import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { getFeatureBySlug } from '@/lib/featuresData';
import {
  Sparkles,
  FileText,
  Zap,
  Languages,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock,
  Image,
  MessageSquare,
  Wand2,
  Copy,
  Share2,
  Link,
  TrendingUp,
  Globe,
  QrCode,
  Smartphone,
  Crown,
  DollarSign,
  Home,
  Bed,
  Bath,
  Ruler,
  MapPin,
  Check,
  X,
  Minus,
} from 'lucide-react';
import { Users, Building2, Mail, Play } from 'lucide-react';
import { PhotoComparisonSlider } from './PhotoComparisonSlider';
import { DemoVideosSection } from './DemoVideosSection';
import { EnhancementMorphSection } from './EnhancementMorphSection';
import { TryItNowWidget } from './TryItNowWidget';

// Import local images for before/after comparisons
import clutterBefore from '@/assets/clutter-before.jpg';
import clutterAfter from '@/assets/clutter-after.jpg';
import goldenHourBefore from '@/assets/golden-hour-before.jpg';
import goldenHourAfter from '@/assets/golden-hour-after.jpg';
import stagingBefore from '@/assets/staging-before.jpg';
import stagingAfter from '@/assets/staging-after.jpg';
import skyLawnBefore from '@/assets/sky-lawn-before.jpg';
import skyLawnAfter from '@/assets/sky-lawn-after.jpg';
import qualityBefore from '@/assets/quality-before.jpg';
import qualityAfter from '@/assets/quality-after.jpg';
import customPromptBefore from '@/assets/custom-prompt-before.jpg';
import customPromptAfter from '@/assets/custom-prompt-after.jpg';
import heroAfter from '@/assets/hero-after.jpg';
import heroBefore from '@/assets/hero-before.jpg';

// Sample before/after images for the comparison slider — ordered by visual
// "wow": the most dramatic transformations fill the first (most-seen) row.
const sampleImages = [
  {
    original: stagingBefore,
    enhanced: stagingAfter,
    label: 'Virtual Staging',
  },
  {
    original: skyLawnBefore,
    enhanced: skyLawnAfter,
    label: 'Sky & Lawn Replacement',
  },
  {
    original: goldenHourBefore,
    enhanced: goldenHourAfter,
    label: 'Golden Hour',
  },
  {
    original: clutterBefore,
    enhanced: clutterAfter,
    label: 'Clutter Removal',
  },
  {
    original: qualityBefore,
    enhanced: qualityAfter,
    label: 'Photo Quality Enhancement',
  },
  {
    original: customPromptBefore,
    enhanced: customPromptAfter,
    label: 'Custom AI Prompt',
  },
];

// Key features with more detail
// Ordered by conversion impact: outcome-driving tools (photos → copy →
// distribution) first, then convenience/onboarding features.
const features = [
  {
    icon: Wand2,
    title: 'AI Photo Enhancement',
    description: 'Transform amateur photos into professional-grade images with one click.',
    benefits: ['Sky replacement', 'Color correction', 'Remove clutter'],
  },
  {
    icon: FileText,
    title: 'AI Descriptions That Sell',
    description: 'Generate persuasive, SEO-optimized descriptions that highlight your property\'s best features and drive buyer interest.',
    benefits: ['Increase inquiries', 'SEO optimized', 'Emotionally engaging'],
  },
  {
    icon: Share2,
    title: 'Social Media Ready',
    description: 'Get perfectly formatted posts for Facebook Marketplace, Instagram feed & stories, and WhatsApp broadcast lists.',
    benefits: ['Facebook posts', 'Instagram stories', 'Marketplace listings'],
  },
  {
    icon: Link,
    title: 'Import from Any URL',
    description: 'Already have a listing online? Just paste the URL and we\'ll import all photos and details automatically.',
    benefits: ['Auto-import photos', 'Extract property details', 'One-click setup'],
  },
  {
    icon: Languages,
    title: 'Multi-Language Support',
    description: 'Reach Spanish-speaking and relocating buyers in their own language.',
    benefits: ['28 languages', 'Spanish-ready'],
  },
];

// How it works - simplified
const steps = [
  { number: '1', title: 'Upload or Import', description: 'Add your property photos', detail: 'Upload up to 10 images or import from URL' },
  { number: '2', title: 'Add Details', description: 'Chat about your property', detail: 'AI asks smart questions' },
  { number: '3', title: 'Get Listing', description: 'AI generates everything', detail: 'Ready in about 10 minutes' },
  { number: '4', title: 'Sell or Rent — Instantly', description: 'Publish to every channel at once', detail: 'Then broadcast it while it\'s still hot' },
];

// Comparison table — honest, decision-driving criteria a listing agent actually
// weighs. Competitors are credited where they genuinely deliver (a photographer
// really does shoot great photos & a copywriter writes good copy) — being fair is
// what makes the wins that matter (cost, speed, all-in-one, scale) believable.
type CompareColKey = 'lb' | 'diy' | 'ai' | 'pro';
type CompareValue = string | boolean | 'partial';

const compareColumns: { key: CompareColKey; label: string; highlight?: boolean }[] = [
  { key: 'lb', label: 'ListBright', highlight: true },
  { key: 'diy', label: 'DIY (phone)' },
  { key: 'ai', label: 'ChatGPT + Canva' },
  { key: 'pro', label: 'Photographer + copywriter' },
];

const compareRows: ({ label: string; isText?: boolean } & Record<CompareColKey, CompareValue>)[] = [
  { label: "Time to relaunch a listing that isn't selling", isText: true, lb: '~10 min', diy: '3+ hrs', ai: '~1 hr', pro: '3–5 days' },
  { label: 'Cost per listing', isText: true, lb: 'from $6', diy: 'Your time', ai: '$20+/mo', pro: '$300+' },
  { label: 'Magazine-quality photos', lb: true, diy: false, ai: 'partial', pro: true },
  { label: 'Virtual staging', lb: true, diy: false, ai: false, pro: 'partial' },
  { label: 'Persuasive, SEO-ready copy', lb: true, diy: false, ai: true, pro: true },
  { label: 'Microsite, flyer, video & QR', lb: true, diy: false, ai: false, pro: 'partial' },
  { label: 'Spanish + 28 other languages', lb: true, diy: false, ai: 'partial', pro: false },
  { label: 'Scales across every listing you carry', lb: true, diy: false, ai: false, pro: false },
];

// Single comparison cell: yes (emerald check), partial (amber dash), no (muted X).
function CompareCell({ value }: { value: boolean | 'partial' }) {
  if (value === true) {
    return (
      <div className="flex justify-center">
        <div className="w-6 h-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
          <Check className="w-3.5 h-3.5 text-emerald-600" />
        </div>
      </div>
    );
  }
  if (value === 'partial') {
    return (
      <div className="flex justify-center">
        <div className="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center">
          <Minus className="w-3.5 h-3.5 text-amber-500" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-center">
      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
        <X className="w-3.5 h-3.5 text-muted-foreground/40" />
      </div>
    </div>
  );
}

export function EnhancedHeroSection({ onGetStarted }: { onGetStarted: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setPendingImportUrl } = useCreateListing();
  const cinematicFeature = getFeatureBySlug('cinematic-video');
  const cinematicSampleVideo = cinematicFeature?.sampleVideo ?? '/sample-cinematic.mp4';
  const cinematicSampleVideoWebm = cinematicSampleVideo.endsWith('.mp4')
    ? cinematicSampleVideo.replace(/\.mp4$/, '.webm')
    : undefined;
  const cinematicSourcePhotos = cinematicFeature?.sampleVideoSourcePhotos ?? [];
  const [heroUrl, setHeroUrl] = useState('');
  const STARTER_USD = 30;

  // Scroll reveal refs for each section
  const heroRef = useScrollReveal<HTMLElement>();
  const statsRef = useScrollReveal<HTMLElement>({ delay: 100 });
  const featuresRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const galleryRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const descriptionRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const cinematicRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const micrositeRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const platformRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const proofRef = useScrollReveal<HTMLElement>({ delay: 50 });
  const ctaRef = useScrollReveal<HTMLElement>({ delay: 50 });
  
  // Determine CTA text based on user state.
  // While billing data is loading for a signed-in user, show the neutral
  // "Create Your Listing" label so we never flash an older variant.
  const getCtaText = () => {
    if (!user) return 'Get My Listing Kit';
    return 'Create Your Listing';
  };

  // Smart primary CTA: skip the sales detour. Logged-in users go straight to
  // the create flow; logged-out users go to /auth and are forwarded into the
  // create flow once authenticated.
  const handlePrimaryCreateClick = () => {
    if (user) {
      onGetStarted();
    } else {
      navigate('/auth?next=create');
    }
  };

  // URL-import shortcut: stash the URL and route into the create flow. The
  // chat picks it up from CreateListingContext / sessionStorage and auto-runs
  // the import on mount.
  const handleHeroUrlImport = () => {
    const url = String(heroUrl ?? '').trim();
    if (!url) return;
    setPendingImportUrl(url);
    if (user) {
      onGetStarted();
    } else {
      navigate('/auth?next=create');
    }
  };

  return (
    <div className="gradient-warm">
      {/* Hero Section — full-bleed image with overlaid copy */}
      <section ref={heroRef} className="relative w-full overflow-hidden">
        {/* Background: interactive before/after comparison */}
        <HeroBeforeAfter beforeSrc={heroBefore} afterSrc={heroAfter} />
        {/* Readability gradient: dark on left, fading right */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10 lg:from-black/80 lg:via-black/40 lg:to-transparent"
        />
        {/* Bottom fade for graceful transition into next section */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-background pointer-events-none"
        />

        {/* Content */}
        <div className="pointer-events-none relative container max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 lg:py-40 min-h-[560px] sm:min-h-[640px] lg:min-h-[720px] flex items-center">
          <div className="max-w-2xl space-y-6 lg:space-y-8 text-white">
            <div className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] text-white/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.5)]">
              <Sparkles className="w-3.5 h-3.5" />
              For Listing Agents
            </div>

            <h1 className="text-[2rem] sm:text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-[1.05] text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.45)]">
              Just cut the price?{' '}
              <span className="text-sky-300">Relaunch the listing, not just the number.</span>
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-white/85 max-w-xl leading-relaxed [text-shadow:0_1px_12px_rgba(0,0,0,0.5)]">
              Don't waste the relist on the same tired photos. Go live with a brand-new listing —{' '}
              <span className="font-semibold text-white">photos, staging, video, microsite and flyer</span>{' '}
              — in 10 minutes. Win the owner. Keep the portal spend.
            </p>

            <div className="pt-2">
              <Button
                size="lg"
                onClick={handlePrimaryCreateClick}
                className="pointer-events-auto h-14 px-8 text-base sm:text-lg font-bold rounded-full bg-primary hover:bg-primary/95 text-primary-foreground shadow-2xl ring-1 ring-white/20 transition-transform hover:scale-[1.02] active:scale-[0.98]"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Relaunch a listing free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-white/80 text-sm mt-3 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]">
                Free to try · No card required · Plans from ${STARTER_USD}/mo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What agents get out of a relaunch — no invented performance numbers */}
      <section ref={statsRef} className="border-y border-border/40 bg-card/50">
        <div className="container max-w-5xl mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { k: '~10 min', v: 'To Rebuild A Full Listing' },
              { k: 'Every asset', v: 'Photos, Video, Microsite, Flyer' },
              { k: 'Minutes', v: 'From Phone Snap To Live Listing' },
              { k: '5–50', v: 'Active Listings Per Plan' },
            ].map((s) => (
              <div key={s.v} className="space-y-1.5">
                <div className="text-2xl sm:text-3xl font-display font-bold text-foreground">{s.k}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery — moved up: strongest visual hook right after hero */}
      <section ref={galleryRef} className="bg-card border-y border-border/40 section-padding">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Give the Same House a <span className="text-gradient">Second First Impression</span>
            </h2>
            <p className="text-muted-foreground">
              Drag any slider — this is what your existing photo set looks like after a relaunch,
              with no reshoot and no photographer to rebook. Enhancement and staging refine light,
              clutter and furnishing — the architecture, layout and fixed elements of the property
              stay exactly as they are.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sampleImages.map((image, index) => (
              <div key={index} className="space-y-3">
                <div className="rounded-xl overflow-hidden shadow-lg border border-border/50">
                  <PhotoComparisonSlider
                    originalSrc={image.original}
                    enhancedSrc={image.enhanced}
                  />
                </div>
                <p className="text-center text-sm font-medium text-muted-foreground">
                  {image.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live AI Enhancement morph showcase — first of the two demos */}
      <EnhancementMorphSection />

      {/* Try Your Photo — quick enhancement demo */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background via-primary/5 to-muted/30 border-y border-border/40">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-6xl mx-auto px-4 py-16 sm:py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Editorial column */}
            <div className="lg:col-span-4 text-center lg:text-left">
              <Badge variant="outline" className="mb-4 bg-card/60 backdrop-blur">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Quick Enhancement
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 leading-tight">
                Try Your Photo <span className="text-gradient">Right Now</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Upload any property photo and see AI enhancement in seconds — no account needed.
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
                {['Sky replacement', 'Clutter removal', 'Color correction'].map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted/60 px-3 py-1.5 rounded-full"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Widget column */}
            <div className="lg:col-span-8">
              <TryItNowWidget
                inline
                onGetStarted={onGetStarted}
                sampleOriginalSrc={clutterBefore}
                sampleEnhancedSrc={clutterAfter}
              />
            </div>
          </div>
        </div>
      </section>

      {/* URL Import — elevated; sits BETWEEN the two demos so we never stack two
          videos back-to-back. Its gradient bridges the light morph section above
          into the muted walkthrough section below for a seamless transition. */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background via-primary/5 to-muted/30">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container max-w-3xl mx-auto px-4 py-20 sm:py-24 relative">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <Badge variant="outline" className="mb-4 bg-card/60 backdrop-blur">
              <Link className="w-3.5 h-3.5 mr-1.5" />
              Instant Import — No Re-typing
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-3">
              Already Have a Listing <span className="text-gradient">Online?</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Paste the URL — we'll pull every photo and detail in seconds, then upgrade the whole listing for you.
            </p>
          </div>

          {/* Elevated glass input card — lifts off the band to add depth between the two flat video frames */}
          <div className="relative rounded-2xl bg-card/80 backdrop-blur-xl border border-border/60 shadow-elevated ring-1 ring-foreground/5 p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Link className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="url"
                  inputMode="url"
                  placeholder="Paste listing URL (Zillow, Property Finder, Bayut, Idealista…)"
                  value={heroUrl}
                  onChange={(e) => setHeroUrl(e.target.value ?? '')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleHeroUrlImport();
                    }
                  }}
                  className="h-12 pl-10 text-base bg-background"
                  aria-label="Paste an existing listing URL to import"
                />
              </div>
              <Button
                variant="hero"
                size="lg"
                className="h-12 sm:w-auto whitespace-nowrap"
                onClick={handleHeroUrlImport}
                disabled={!String(heroUrl ?? '').trim()}
              >
                <Zap className="w-4 h-4 mr-1.5" />
                Import & Enhance
              </Button>
            </div>

            {/* Trust chips */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
              {['Works with most listing sites', 'Imports photos & details', 'Done in under a minute'].map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted/60 px-3 py-1.5 rounded-full"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Videos — full product walkthrough; second of the two demos */}
      <DemoVideosSection />

      {/* Who it's for — agents in slow markets. No fabricated proof, just the fit. */}
      <section ref={proofRef} className="section-padding">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              Built For Competitive Markets
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
              Made for agents where the best-presented listing wins the owner and the buyer
            </h2>
            <p className="text-muted-foreground text-lg">
              Inventory is up, buyers are picky, and the listing that looks best gets the showing.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                title: 'You just took a price cut',
                body: 'A lower number resets the search alerts. Relaunch with photos and copy that make the new price look like a steal.',
              },
              {
                title: 'The listing expired or stopped attracting buyers',
                body: 'Same property, brand-new presentation: staged rooms, golden-hour exterior, fresh description, microsite and flyer. Exclusive listings deserve premium presentation.',
              },
              {
                title: 'You are winning the next listing',
                body: 'When the owner is choosing between brokers, walk in with the presentation already built.',
              },
            ].map((item) => (
              <div key={item.title} className="p-7 rounded-3xl bg-card border border-border/60 text-left">
                <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features - More detailed but still clean */}
      <section ref={featuresRef} className="section-padding">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="outline" className="mb-4">The Suite</Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
              Everything a <span className="text-gradient">relaunch needs</span>
            </h2>
            <p className="text-muted-foreground">
              Photos, virtual staging, descriptions, video, microsite and flyer — one upload
              rebuilds every asset the listing goes back live with.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="flex gap-5 p-6 rounded-2xl bg-card border border-border/60 card-premium">
                  <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-display font-semibold text-lg">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {feature.benefits.map((benefit) => (
                        <span key={benefit} className="inline-flex items-center text-xs bg-muted/60 px-2.5 py-1 rounded-full">
                          <CheckCircle2 className="w-3 h-3 mr-1.5 text-primary" />
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cinematic Video Section */}
      <section ref={cinematicRef} className="section-padding bg-gradient-to-b from-background via-muted/20 to-background border-y border-border/40">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-[0.92fr_1.08fr] gap-8 lg:gap-14 items-center">
            <div className="max-w-xl">
              <Badge variant="outline" className="mb-5 bg-card/70 backdrop-blur border-primary/20 text-foreground/80">
                <Play className="w-3.5 h-3.5 mr-1.5 text-primary" />
                Cinematic Video
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
                Add <span className="text-gradient">cinematic motion</span> to your listing
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                A premium video add-on for standout homes, social content, and higher-converting presentation.
              </p>

              <div className="flex flex-wrap gap-2.5 mb-9">
                {[
                  { icon: Sparkles, label: 'AI motion' },
                  { icon: DollarSign, label: '$5 add-on' },
                  { icon: Clock, label: 'Ready in minutes' },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/80 py-1.5 pl-1.5 pr-4 text-sm font-medium text-foreground/80 shadow-sm backdrop-blur transition-colors hover:border-primary/30"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    {label}
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <Button
                    variant="hero"
                    size="lg"
                    className="sm:w-auto shadow-elevated"
                    onClick={handlePrimaryCreateClick}
                  >
                    Create Listing
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Add it when your listing is ready.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-primary/10 via-transparent to-accent/10 blur-3xl rounded-[2rem] pointer-events-none" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-border/60 bg-black shadow-elevated">
                <video
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full aspect-video object-cover"
                  aria-label="Sample cinematic property video — use the play and pause controls to preview"
                >
                  <source src={cinematicSampleVideo} type="video/mp4" />
                  {cinematicSampleVideoWebm && (
                    <source src={cinematicSampleVideoWebm} type="video/webm" />
                  )}
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex items-center justify-between gap-4 pt-3 px-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  Sample cinematic preview
                </span>
                <span className="hidden sm:inline">Built from real listing photos</span>
              </div>
              {cinematicSourcePhotos.length > 0 && (
                <div className="mt-4 rounded-2xl border border-border/40 bg-card/70 p-4 sm:p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-[11px] sm:text-xs font-medium text-muted-foreground uppercase tracking-[0.2em]">
                      Photos used to create this video
                    </p>
                    <span className="hidden sm:inline text-[11px] text-muted-foreground">
                      One cinematic clip is generated from each image.
                    </span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                    {cinematicSourcePhotos.map((photo) => (
                      <figure key={photo.src} className="space-y-1.5">
                        <div className="aspect-[4/3] rounded-lg overflow-hidden border border-border/40 bg-muted">
                          <img
                            src={photo.src}
                            alt={photo.label}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            width={400}
                            height={300}
                          />
                        </div>
                        <figcaption className="text-[10px] sm:text-xs text-muted-foreground text-center truncate">
                          {photo.label}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* AI Description Example Section */}
      <section ref={descriptionRef} className="section-padding">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              AI Descriptions
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
              Descriptions That <span className="text-gradient">Sell</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Buyers decide in seconds. Our AI turns a plain list of facts into an emotional,
              SEO-optimized story — copy ready to paste into the portals and your broadcast lists.
            </p>
          </div>

          <div className="relative grid lg:grid-cols-2 gap-6 lg:gap-14 items-stretch">
            {/* Before — raw input */}
            <div className="flex flex-col">
              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Your input
              </span>
              <div className="flex-1 p-6 sm:p-7 rounded-3xl bg-muted/40 border border-border/60">
                <ul className="space-y-3.5 text-sm text-muted-foreground">
                  {['3 bed, 2 bath house', '180 m² with garden', 'Recently renovated kitchen', 'Private garage & driveway', '$450,000'].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Transform badge — visually links input → output on desktop */}
            <div
              aria-hidden="true"
              className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 items-center justify-center"
            >
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-elevated ring-4 ring-background">
                <Wand2 className="w-6 h-6" />
              </div>
            </div>

            {/* After — polished, rendered listing card */}
            <div className="flex flex-col">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                ListBright AI
              </span>
              <div className="flex-1 rounded-3xl bg-card border border-border/60 shadow-elevated overflow-hidden">
                {/* Card toolbar */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-border/60 bg-muted/30">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Languages className="w-3.5 h-3.5 text-primary" />
                    English · 28 languages
                  </div>
                  <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div className="p-6 sm:p-7 space-y-4">
                  <h3 className="font-display font-bold text-lg sm:text-xl text-foreground">
                    Stunning Family Home in a Prime Neighbourhood
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Welcome to this beautifully designed 3-bedroom family home with a spacious garden
                    and private garage. The open-plan living area flows seamlessly into the landscaped
                    outdoor space — perfect for relaxing and entertaining.
                  </p>
                  {/* Stat row */}
                  <div className="grid grid-cols-3 divide-x divide-border/60 rounded-xl border border-border/60 overflow-hidden text-center">
                    <div className="py-2.5">
                      <Bed className="w-4 h-4 mx-auto text-primary mb-1" />
                      <p className="text-xs text-muted-foreground">3 Beds</p>
                    </div>
                    <div className="py-2.5">
                      <Bath className="w-4 h-4 mx-auto text-primary mb-1" />
                      <p className="text-xs text-muted-foreground">2 Baths</p>
                    </div>
                    <div className="py-2.5">
                      <Ruler className="w-4 h-4 mx-auto text-primary mb-1" />
                      <p className="text-xs text-muted-foreground">180 m²</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <span className="text-xl font-display font-bold text-primary">$450,000</span>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {['#FamilyHome', '#ModernLiving', '#DreamHome'].map((tag) => (
                        <span key={tag} className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefit chips */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
            {['Emotionally engaging', 'SEO-optimized', '28+ languages', 'Ready to paste anywhere'].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted/60 px-3 py-1.5 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                {b}
              </span>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button variant="hero" size="lg" onClick={onGetStarted} className="gap-2">
              <Wand2 className="w-5 h-5" />
              Generate Your Listing Description
            </Button>
            <p className="mt-3 text-sm text-muted-foreground">
              Free to try · No card required · Ready in seconds
            </p>
          </div>
        </div>
      </section>

      {/* Microsite Section */}
      <section ref={micrositeRef} className="section-padding bg-gradient-to-br from-accent/5 via-background to-primary/5">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              Share Instantly
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
              Every Listing Gets Its Own <span className="text-gradient">Microsite</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Every listing automatically gets a beautiful, shareable web page — perfect for dropping
              into a WhatsApp broadcast, sending to buyers, or printing as a QR code.
            </p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 px-3 py-1.5 rounded-full">
              <Crown className="w-3.5 h-3.5" />
              Available on Growth &amp; Team plans
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Features */}
            <div className="space-y-4">
              {[
                { icon: Link, title: 'Shareable Link', desc: 'Get a unique URL for each listing. Share via email, SMS, social media, or anywhere.' },
                { icon: QrCode, title: 'Auto-Generated QR Code', desc: 'Print the QR code on flyers, signs, or business cards. Buyers scan and view instantly.' },
                { icon: Smartphone, title: 'Mobile-Optimized', desc: 'Looks stunning on any device. Enhanced photos, full descriptions, and easy contact options.' },
                { icon: Languages, title: 'Multi-Language Ready', desc: 'Visitors can switch languages on the microsite — useful for relocation and international buyers.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex gap-4 p-5 sm:p-6 rounded-2xl bg-card border border-border/60 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Microsite preview framed in a browser window */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-elevated border border-border/60 bg-card ring-1 ring-foreground/5">
                {/* Browser chrome */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/40">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                  <div className="ml-2 flex-1 flex items-center gap-1.5 px-3 py-1 rounded-md bg-background/80 border border-border/50 text-[11px] text-muted-foreground">
                    <Link className="w-3 h-3 shrink-0" />
                    listbright.site/your-listing
                  </div>
                </div>

                {/* Hero image area with real photo */}
                <div className="aspect-video relative">
                  <img
                    src={heroAfter}
                    alt="AI-enhanced property listing photo showing a modern home exterior"
                    width={1280}
                    height={720}
                    fetchPriority="high"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  {/* Price overlay */}
                  <div className="absolute bottom-3 left-4">
                    <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-foreground/50 backdrop-blur text-sm text-background font-medium">
                      $450,000
                    </div>
                  </div>
                </div>

                {/* Stats bar */}
                <div className="grid grid-cols-4 divide-x divide-border text-center py-3 bg-card">
                  <div className="px-2">
                    <Home className="w-4 h-4 mx-auto text-primary mb-1" />
                    <p className="text-xs text-muted-foreground">House</p>
                  </div>
                  <div className="px-2">
                    <Bed className="w-4 h-4 mx-auto text-primary mb-1" />
                    <p className="text-xs text-muted-foreground">3 Beds</p>
                  </div>
                  <div className="px-2">
                    <Bath className="w-4 h-4 mx-auto text-primary mb-1" />
                    <p className="text-xs text-muted-foreground">2 Baths</p>
                  </div>
                  <div className="px-2">
                    <Ruler className="w-4 h-4 mx-auto text-primary mb-1" />
                    <p className="text-xs text-muted-foreground">180 m²</p>
                  </div>
                </div>

                {/* Content preview */}
                <div className="p-4 space-y-2 bg-card">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" />
                    <span>Your City</span>
                  </div>
                  <div className="h-2.5 bg-muted rounded w-full" />
                  <div className="h-2.5 bg-muted rounded w-4/5" />
                  <div className="h-2.5 bg-muted rounded w-3/5" />
                </div>

                {/* Language selector preview */}
                <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded font-medium">EN</span>
                    <span className="text-xs px-2 py-1 bg-card border border-border rounded">ES</span>
                    <span className="text-xs px-2 py-1 bg-card border border-border rounded">FR</span>
                    <span className="text-xs text-muted-foreground">+25 more</span>
                  </div>
                </div>
              </div>

              {/* Floating QR chip — adds depth and reinforces the QR feature */}
              <div className="hidden md:flex absolute -bottom-5 -left-5 items-center gap-2.5 rounded-2xl bg-card/95 backdrop-blur px-3.5 py-2.5 shadow-elevated border border-border/60">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10">
                  <QrCode className="w-5 h-5 text-primary" />
                </div>
                <div className="leading-tight">
                  <p className="text-xs font-semibold">Scan to view</p>
                  <p className="text-[10px] text-muted-foreground">Instant mobile preview</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 text-center">
            <Button variant="outline" size="lg" onClick={() => navigate('/pricing')} className="gap-2">
              See Plan Features
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works — pinned scrollytelling: the section pins while the steps
          advance with scroll, then releases into the next section. */}
      <HowItWorks onGetStarted={onGetStarted} />

      {/* Owner-vs-agent decision is consolidated into one combined section at the
          end of the page (see "Get Started" below). */}
      {/* Share Everywhere — the distribution story: one listing, every channel */}
      <section ref={platformRef} className="section-padding bg-card border-y border-border/40">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              One Listing, Everywhere
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4 tracking-tight">
              Share it anywhere <span className="text-gradient">buyers are</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Every listing is ready to publish across channels in a click — no reformatting,
              no extra tools.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Shareable microsite', icon: <Link className="w-5 h-5" /> },
              { label: 'QR code', icon: <QrCode className="w-5 h-5" /> },
              {
                label: 'Facebook',
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                ),
              },
              {
                label: 'Instagram',
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                ),
              },
              {
                label: 'WhatsApp',
                icon: (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
                  </svg>
                ),
              },
              { label: 'PDF flyer', icon: <FileText className="w-5 h-5" /> },
              { label: 'Video slideshow', icon: <Play className="w-5 h-5" /> },
              { label: 'Email', icon: <Mail className="w-5 h-5" /> },
            ].map((channel) => (
              <div
                key={channel.label}
                className="flex flex-col items-center justify-center gap-2.5 p-4 sm:p-5 rounded-2xl bg-muted/40 border border-border/60"
              >
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  {channel.icon}
                </div>
                <span className="text-xs font-medium text-muted-foreground text-center">{channel.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Languages className="w-4 h-4 text-primary" />
              Auto-translated into 28+ languages, including Arabic
            </span>
          </div>
        </div>
      </section>

      {/* Compact Comparison Widget */}
      <section className="section-padding bg-muted/30 border-y border-border/40">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              How We Compare
            </p>
            <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">
              All-in-one, for a <span className="text-gradient">fraction of the cost</span>
            </h2>
            <p className="text-muted-foreground text-lg mt-4">
              Portal placement is expensive — make every listing count with a presentation that stops the scroll.
            </p>
          </div>

          {/* Comparison table — ListBright column highlighted as a raised band */}
          <div className="overflow-x-auto pb-2">
            <table className="w-full min-w-[640px] border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th scope="col" className="w-[30%]" />
                  {compareColumns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={[
                        'py-4 px-3 align-bottom text-center w-[17.5%]',
                        col.highlight ? 'rounded-t-2xl bg-primary/8 border-x border-t border-primary/20' : '',
                      ].join(' ')}
                    >
                      {col.highlight && (
                        <span className="inline-flex items-center gap-1 mb-2 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5">
                          <Sparkles className="w-2.5 h-2.5" />
                          Recommended
                        </span>
                      )}
                      <span
                        className={
                          col.highlight
                            ? 'block font-display font-bold text-primary leading-tight'
                            : 'block font-medium text-muted-foreground leading-tight'
                        }
                      >
                        {col.label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compareRows.map((row, i) => {
                  const isLast = i === compareRows.length - 1;
                  return (
                    <tr key={row.label}>
                      <th scope="row" className="py-4 pr-4 text-left font-medium align-middle border-b border-border/40">
                        {row.label}
                      </th>
                      {compareColumns.map((col) => {
                        const val = row[col.key];
                        return (
                          <td
                            key={col.key}
                            className={[
                              'py-4 px-3 text-center align-middle border-b border-border/40',
                              col.highlight ? 'bg-primary/8 border-x border-primary/20' : '',
                              col.highlight && isLast ? 'rounded-b-2xl' : '',
                            ].join(' ')}
                          >
                            {row.isText ? (
                              <span
                                className={
                                  col.highlight
                                    ? 'text-sm font-semibold text-primary'
                                    : 'text-xs text-muted-foreground'
                                }
                              >
                                {val as string}
                              </span>
                            ) : (
                              <CompareCell value={val as boolean | 'partial'} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <button
              onClick={() => navigate('/compare')}
              className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              See full comparison <ChevronRight className="w-4 h-4" />
            </button>
            <RouterLink
              to="/best-virtual-staging-software"
              className="text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
            >
              Best virtual staging software (2026) <ChevronRight className="w-4 h-4" />
            </RouterLink>
          </div>
        </div>
      </section>

      {/* Get Started — single agent-focused closing CTA. */}
      <section ref={ctaRef} className="section-padding">
        <div className="container max-w-3xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-4">
              Get Started
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 tracking-tight">
              Relaunch your next price cut{' '}
              <span className="text-gradient">this afternoon.</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              Built for listing agents and teams. No learning curve, no design skills, no reshoot.
            </p>
          </div>

          <div className="flex flex-col p-7 sm:p-9 rounded-3xl bg-card border-2 border-primary/30 shadow-elevated">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-bold text-xl">For listing agents</h3>
                <p className="text-sm text-muted-foreground">Agents, brokers, agencies &amp; teams</p>
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-display font-bold">${STARTER_USD}</span>
              <span className="text-muted-foreground text-sm">/mo</span>
            </div>
            <p className="text-xs font-medium text-muted-foreground mt-1.5 mb-6">
              Plans scale from 5 up to 50 active listings
            </p>

            <ul className="space-y-3 mb-7">
              {[
                'Rebuild photos, staging, copy, video, microsite & flyer per listing',
                'Team collaboration & custom branding',
                'Annual plans save up to 2 months',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="hero" size="lg" className="flex-1" onClick={handlePrimaryCreateClick}>
                <Sparkles className="w-5 h-5 mr-2" />
                {getCtaText()}
              </Button>
              <Button variant="outline" size="lg" className="flex-1" onClick={() => navigate('/pricing')}>
                <Users className="w-5 h-5 mr-2" />
                See Plans
              </Button>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Plans from ${STARTER_USD}/mo · Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}

// "How It Works" — calm, static 4-step layout. No scroll-jacking, no rotating
// cards. A connected numbered timeline so the flow reads at a glance.
function HowItWorks({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="bg-muted/30 py-20 sm:py-24">
      <div className="container max-w-6xl mx-auto px-4">
        <div className="text-center mb-12 lg:mb-16 max-w-2xl mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary mb-3">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-3 tracking-tight">
            From Photos to <span className="text-gradient">Sold</span> in {steps.length} Steps
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            A few phone photos in, a published listing out — in about 10 minutes.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line — desktop only, horizontal across the row */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />

          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
            {steps.map((step, i) => (
              <li
                key={step.number}
                className="relative rounded-2xl bg-card border border-border/60 shadow-sm p-6 sm:p-7 text-center animate-fade-in"
                style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'backwards' }}
              >
                <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground font-display font-bold text-2xl shadow-lg shadow-primary/30 ring-8 ring-muted/30">
                  {step.number}
                </div>
                <h3 className="font-display font-bold text-lg sm:text-xl mb-2">{step.title}</h3>
                <p className="text-muted-foreground text-sm mb-1.5">{step.description}</p>
                <p className="text-xs text-muted-foreground/70">{step.detail}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="text-center mt-12">
          <Button variant="hero" size="lg" onClick={onGetStarted} className="gap-2">
            <Zap className="w-5 h-5" />
            Create Your Listing
          </Button>
        </div>
      </div>
    </section>
  );
}

// Interactive before/after hero comparison — fills the section background.
function HeroBeforeAfter({ beforeSrc, afterSrc }: { beforeSrc: string; afterSrc: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(58); // % from left where divider sits
  const draggingRef = useRef(false);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      updateFromClientX(e.clientX);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [updateFromClientX]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 select-none"
      onPointerDown={(e) => {
        draggingRef.current = true;
        updateFromClientX(e.clientX);
      }}
    >
      {/* After image (full) */}
      <img
        src={afterSrc}
        alt="Professionally enhanced real estate listing"
        className="absolute inset-0 w-full h-full object-cover"
        fetchPriority="high"
        decoding="async"
        draggable={false}
      />
      {/* Before image clipped */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={beforeSrc}
          alt="Original unedited listing photo"
          className="absolute inset-0 w-full h-full object-cover"
          decoding="async"
          draggable={false}
        />
      </div>

      {/* Before / After corner labels */}
      <div className="pointer-events-none absolute top-4 left-4 z-10 rounded-full bg-black/60 backdrop-blur px-3 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-white/90 ring-1 ring-white/20">
        Before
      </div>
      <div className="pointer-events-none absolute top-4 right-4 z-10 rounded-full bg-primary/90 backdrop-blur px-3 py-1 text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-primary-foreground ring-1 ring-white/30">
        After
      </div>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 z-10 w-px bg-white/90 shadow-[0_0_24px_rgba(255,255,255,0.5)]"
        style={{ left: `${pos}%` }}
      >
        <div
          className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white shadow-2xl ring-2 ring-primary flex items-center justify-center cursor-ew-resize touch-none"
          onPointerDown={(e) => {
            e.stopPropagation();
            draggingRef.current = true;
            (e.target as Element).setPointerCapture?.(e.pointerId);
          }}
          aria-label="Drag to compare before and after"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 3 12 9 6" style={{ opacity: 0 }} />
          </svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary -ml-1">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </div>
      </div>
    </div>
  );
}
